import { API_URL } from "@/config/api";
import { getSession } from "@/utils/session";
import { convertCurrency, CurrencyCode } from "@/utils/currencyConverter";

/**
 * Cart utility functions for managing cart data in localStorage
 */

export interface CartItem {
  domains?: Array<{
    name: string;
    price: number;
    currency: string;
    renewPrice?: number;
    available: boolean;
    period?: number;
    privacy?: boolean;
  }>;
  domain?: { // keep for backward compatibility temporarily
    name: string;
    price: number;
    currency: string;
    renewPrice?: number;
    available: boolean;
    period?: number;
    privacy?: boolean;
  };
  services?: Array<{
    type: string;
    name: string;
    planId?: number;
    price: number;
    currency: string;
    period?: number;
  }>;
}

export interface CartData {
  items: CartItem;
  timestamp: number;
}

/**
 * Get cart data from localStorage
 */
export function getCartData(): CartData | null {
  try {
    const cartDataStr = localStorage.getItem('cartData');
    if (!cartDataStr) return null;

    const cartData = JSON.parse(cartDataStr);
    return cartData;
  } catch (error) {
    console.error('Error reading cart data:', error);
    return null;
  }
}

/**
 * Save cart data to localStorage and sync with backend if logged in
 */
export async function saveCartData(cartData: CartData, shouldOpenCart: boolean = false): Promise<void> {
  try {
    localStorage.setItem('cartData', JSON.stringify(cartData));

    // Dispatch global event for UI updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cart_updated', { detail: { open: shouldOpenCart } }));
    }

    // Sync with backend if logged in
    const session = getSession();
    if (session?.email) {
      try {
        await fetch(`${API_URL}/auth/cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: session.email,
            cart: cartData
          })
        });
      } catch (err) {
        console.warn("Backend cart sync failed:", err);
      }
    }
  } catch (error) {
    console.error('Error saving cart data:', error);
  }
}

/**
 * Fetch cart from backend
 */
export async function fetchCartFromBackend(email: string): Promise<CartData | null> {
  try {
    const resp = await fetch(`${API_URL}/auth/cart?email=${encodeURIComponent(email)}`);
    const data = await resp.json();
    if (data.success && data.cart) {
      localStorage.setItem('cartData', JSON.stringify(data.cart));
      return data.cart;
    }
    return null;
  } catch (err) {
    console.warn("Failed to fetch cart from backend:", err);
    return null;
  }
}

/**
 * Clear cart data from localStorage and backend if logged in
 */
export async function clearCartData(): Promise<void> {
  try {
    localStorage.removeItem('cartData');

    // Clear in backend if logged in
    const session = getSession();
    if (session?.email) {
      try {
        await fetch(`${API_URL}/auth/cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: session.email,
            cart: null
          })
        });
      } catch (err) {
        console.warn("Backend cart clear failed:", err);
      }
    }
  } catch (error) {
    console.error('Error clearing cart data:', error);
  }
}

/**
 * Add domain to cart
 */
export function addDomainToCart(domain: {
  name: string;
  price: number;
  currency: string;
  renewPrice?: number;
  available: boolean;
  period?: number;
  privacy?: boolean;
}): void {
  const cartData = getCartData() || { items: {}, timestamp: Date.now() };

  if (!cartData.items.domains) {
    cartData.items.domains = [];
  }
  // If exists, update
  const index = cartData.items.domains.findIndex(d => d.name === domain.name);
  if (index >= 0) {
    cartData.items.domains[index] = domain;
  } else {
    cartData.items.domains.push(domain);
  }

  cartData.items.domain = cartData.items.domains[0]; // backward comp
  cartData.timestamp = Date.now();

  saveCartData(cartData, true);
}

export function removeDomainFromCart(domainName: string): void {
  const cartData = getCartData();
  if (!cartData) return;

  if (cartData.items.domains) {
    cartData.items.domains = cartData.items.domains.filter(d => d.name !== domainName);
    if (cartData.items.domains.length > 0) {
      cartData.items.domain = cartData.items.domains[0];
    } else {
      delete cartData.items.domain;
    }
  } else if (cartData.items.domain?.name === domainName) {
    delete cartData.items.domain;
  }

  cartData.timestamp = Date.now();
  saveCartData(cartData, true);
}

/**
 * Add service to cart
 */
export function addServiceToCart(service: {
  type: string;
  name: string;
  planId?: number;
  price: number;
  currency: string;
  period?: number;
}): void {
  const cartData = getCartData() || { items: { services: [] }, timestamp: Date.now() };

  if (!cartData.items.services) {
    cartData.items.services = [];
  }

  // Check if service already exists
  const existingIndex = cartData.items.services.findIndex(
    s => s.type === service.type && s.planId === service.planId
  );

  if (existingIndex >= 0) {
    cartData.items.services[existingIndex] = service;
  } else {
    cartData.items.services.push(service);
  }

  cartData.timestamp = Date.now();
  saveCartData(cartData, true);
}

/**
 * Remove service from cart
 */
export function removeServiceFromCart(serviceType: string, planId?: number): void {
  const cartData = getCartData();
  if (!cartData || !cartData.items.services) return;

  cartData.items.services = cartData.items.services.filter(
    s => !(s.type === serviceType && (!planId || s.planId === planId))
  );

  cartData.timestamp = Date.now();
  saveCartData(cartData);
}

/**
 * Update domain period in cart
 */
export function updateDomainPeriod(domainName: string, period: number): void {
  const cartData = getCartData();
  if (!cartData) return;

  let updated = false;
  if (cartData.items.domains) {
    const idx = cartData.items.domains.findIndex(d => d.name === domainName);
    if (idx >= 0) {
      cartData.items.domains[idx].period = period;
      updated = true;
      if (idx === 0) cartData.items.domain = cartData.items.domains[0];
    }
  } else if (cartData.items.domain && cartData.items.domain.name === domainName) {
    cartData.items.domain.period = period;
    updated = true;
  }

  if (updated) {
    cartData.timestamp = Date.now();
    saveCartData(cartData);
  }
}

/**
 * Calculate total price for a specific domain, applying any special offers
 */
export function calculateDomainTotal(domain: {
  name: string;
  price: number;
  currency: string;
  period?: number;
}): number {
  const period = domain.period || 1;
  let numPrice = typeof domain.price === 'number' ? domain.price : parseFloat(String(domain.price)) || 0;

  // Retroactively fix legacy carts stuck on the 0.05 bug
  if (domain.name.toLowerCase().endsWith('.com.au') && numPrice < 1) {
    numPrice = 12.59;
  }

  // .com.au 3-year special offer
  if (domain.name.toLowerCase().endsWith('.com.au') && period === 3) {
    const firstYearPriceUSD = 0.05;
    const firstYearPriceInCurrency = convertCurrency(firstYearPriceUSD, domain.currency as CurrencyCode);
    return firstYearPriceInCurrency + (numPrice * 2);
  }

  return numPrice * period;
}

/**
 * Calculate total price
 */
export function calculateTotal(cartData: CartData | null): number {
  if (!cartData) return 0;

  let total = 0;

  if (cartData.items.domains && cartData.items.domains.length > 0) {
    cartData.items.domains.forEach(domain => {
      total += calculateDomainTotal(domain);
    });
  } else if (cartData.items.domain) {
    total += calculateDomainTotal(cartData.items.domain);
  }

  if (cartData.items.services) {
    cartData.items.services.forEach(service => {
      const servicePrice = service.price;
      if (servicePrice !== null && servicePrice !== undefined) {
        const numPrice = typeof servicePrice === 'number' ? servicePrice : parseFloat(String(servicePrice)) || 0;
        total += numPrice;
      }
    });
  }

  return total;
}

